import { createSlice } from '@reduxjs/toolkit';
import {
  billingAddressActionTypes,
  statusTypes,
  userTypes,
} from '@/constants/types';
import {
  DeleteShippingAddress,
  EditShippingAddress,
  getAreas,
  getDistricts,
  getDivisions,
  getShippingFees,
  getUpazillas,
  getUserAddresses,
  postShippingAddress,
} from '../thunk/shipping-address-thunk';

// helper functions
export const calculateTotalInstallationFee = (products) => {
  if (!Array.isArray(products) || products.length === 0) return 0;

  return products.reduce((total, product) => {
    if (!product.is_free_installation) {
      return total + (Number(product.installation_fee) || 0);
    }
    return total;
  }, 0);
};

export const getEditFormattedData = (data) => {
  const updatedData = {
    id: data.id,
    name: data.name,
    status: data.status,
    email: data.email,
    phone: data.phone,
    is_default: data.is_default,
    floor: data.floor,
    address: data.address,
    division: {
      id: data.division_id,
      name: data.division,
    },
    district: {
      id: data.district_id,
      name: data.district,
    },
    upazilla: {
      id: data.upazilla_id,
      name: data.upazilla,
    },
    area: {
      id: data.area_id,
      name: data.area,
      postal_code: data.postal_code,
    },
    postal_code: data.postal_code,
  };

  return updatedData;
};

const initialAddress = {
  id: '',
  name: '',
  phone: '',
  email: '',
  address: '',
  floor: '',
  division: '',
  district: '',
  upazilla: '',
  area: '',
  postal_code: '',
  status: false,
  // TODO: dynamically will come from backend
  is_default: true,
};

const initialState = {
  loading: false,
  status: statusTypes.idle,
  postStatus: statusTypes.idle,
  editStatus: statusTypes.idle,
  deleteStatus: statusTypes.idle,

  currentAddress: initialAddress,
  billingAddress: initialAddress,

  isSelectDifferentBilling: false,
  formTitle: '', //  "Shipping Address", "Billing Address"
  currentAddShippingAddressFormType: '', // used to store the current form type (shipping, billing)

  // user all addresses
  userAddresses: [],
  fetchUserAddressesStatus: statusTypes.idle,

  divisions: [],
  districts: [],
  upazillas: [],
  areas: [],
  fetchDivisionStatus: statusTypes.idle,
  fetchDistrictStatus: statusTypes.idle,
  fetchUpazillaStatus: statusTypes.idle,
  fetchAreaStatus: statusTypes.idle,

  // modal
  isOpenUserAddressesModal: false, // list of user addresses
  isOpenAddAddressModal: false, // create new address
  isOpenEditAddressModal: false, // edit address
  isOpenDeleteAddressConfirmationModal: false, // popup for confirmation before deleting an address

  editAddress: null, // used to store the updating address
  selectedAddress: null,

  // common message modal
  message: {
    isSuccess: false,
    isShow: false,
    text: '',
  },

  error: '',
};

const shippingAddressSlice = createSlice({
  name: 'shippingAddress',
  initialState,
  reducers: {
    resetShippingAddressForCache() {
      return initialState;
    },
    updateCurrentAddress: (state, action) => ({
      ...state,
      currentAddress: action.payload,
    }),

    updateBillingAddress: (state, action) => {
      const { data, actionPerform } = action.payload;

      if (actionPerform === billingAddressActionTypes.create) {
        return {
          ...state,
          postStatus: statusTypes.succeeded,
          billingAddress: data,
          isSelectDifferentBilling: true,
        };
      }
      if (actionPerform === billingAddressActionTypes.edit) {
        return {
          ...state,
          editStatus: statusTypes.succeeded,
          billingAddress: data,
          isSelectDifferentBilling: true,
        };
      }
    },

    removeBillingAddress: (state) => ({
      ...state,
      postStatus: statusTypes.idle,
      editStatus: statusTypes.idle,
      billingAddress: initialAddress,
      isSelectDifferentBilling: false,
    }),

    setSelectedAddress: (state, action) => ({
      ...state,
      selectedAddress: action.payload,
    }),

    setIsOpenUserAddressesModal: (state, action) => {
      const { title, data, formType } = action.payload;
      return {
        ...state,
        isOpenUserAddressesModal: data,
        formTitle: title,
        currentAddShippingAddressFormType: formType,
        selectedAddress: state.currentAddress,
      };
    },

    setIsOpenDeleteAddressConfirmationModal: (state, action) => {
      const { data, openModal, formType } = action.payload;
      return {
        ...state,
        isOpenDeleteAddressConfirmationModal: openModal,
        selectedAddress: data,
        currentAddShippingAddressFormType: formType,
      };
    },

    setIsOpenAddAddressModal: (state, action) => {
      const { title, data, formType } = action.payload;
      return {
        ...state,
        isOpenAddAddressModal: data,
        formTitle: title,
        currentAddShippingAddressFormType: formType,
      };
    },

    setIsOpenEditAddressModal: (state, action) => {
      const { title, data, formType, address } = action.payload;
      return {
        ...state,
        isOpenEditAddressModal: data,
        formTitle: title,
        currentAddShippingAddressFormType: formType,
        editAddress: address,
      };
    },

    toggleIsSelectDifferentBillingAddress: (state) => ({
      ...state,
      isSelectDifferentBilling: !state.isSelectDifferentBilling,
    }),

    // resetCheckoutCommonMessage: (state) => ({
    //   ...state,
    //   message: {
    //     text: '',
    //     isShow: false,
    //     isSuccess: false,
    //   },
    // }),

    // setCheckoutCommonMessage: (state, action) => {
    //   const { text, isShow, isSuccess } = action.payload;

    //   return {
    //     ...state,
    //     message: {
    //       text,
    //       isShow,
    //       isSuccess,
    //     },
    //   };
    // },

    updatePostStatus: (state, action) => ({
      ...state,
      postStatus: action.payload,
    }),
    updateEditStatus: (state, action) => ({
      ...state,
      editStatus: action.payload,
    }),
    updateDeleteStatus: (state, action) => ({
      ...state,
      deleteStatus: action.payload,
    }),
    resetPostStatus: (state) => ({
      ...state,
      postStatus: statusTypes.idle,
    }),
    resetEditStatus: (state) => ({
      ...state,
      editStatus: statusTypes.idle,
      editAddress: null,
    }),
    resetDeleteStatus: (state) => ({
      ...state,
      deleteStatus: statusTypes.idle,
      editAddress: null,
    }),
    resetShippingInfoForAuthenticateUser: (state) => ({
      ...state,
      status: statusTypes.idle,
      postStatus: statusTypes.idle,
      editStatus: statusTypes.idle,
      deleteStatus: statusTypes.idle,

      currentAddress: initialAddress,
      userAddresses: [],
      billingAddress: initialAddress,

      isSelectDifferentBilling: false,
      formTitle: '',
      currentAddShippingAddressFormType: '',

      message: {
        isSuccess: false,
        isShow: false,
        text: '',
      },

      error: '',

      // for shipping info
      fetchShippingFeesStatus: statusTypes.idle,

      paymentMethod: null,
    }),
    resetShippingInfoAfterOrderSucceeded: (state) => ({
      ...state,
      status: statusTypes.idle,
      postStatus: statusTypes.idle,
      editStatus: statusTypes.idle,
      deleteStatus: statusTypes.idle,

      currentAddress: initialAddress,
      // userAddresses: [],
      billingAddress: initialAddress,

      isSelectDifferentBilling: false,
      formTitle: '',
      currentAddShippingAddressFormType: '',

      message: {
        isSuccess: false,
        isShow: false,
        text: '',
      },

      error: '',

      // for shipping info
      fetchShippingFeesStatus: statusTypes.idle,

      paymentMethod: null,
    }),
    resetDivisionData: (state) => ({
      ...state,
      fetchDivisionStatus: statusTypes.idle,
      divisions: [],
      fetchDistrictStatus: statusTypes.idle,
      districts: [],
      fetchUpazillaStatus: statusTypes.idle,
      upazillas: [],
      fetchAreaStatus: statusTypes.idle,
      areas: [],
    }),
    resetDistrictData: (state) => ({
      ...state,
      fetchDistrictStatus: statusTypes.idle,
      districts: [],
      fetchUpazillaStatus: statusTypes.idle,
      upazillas: [],
      fetchAreaStatus: statusTypes.idle,
      areas: [],
    }),
    resetUpazillaData: (state) => ({
      ...state,
      fetchUpazillaStatus: statusTypes.idle,
      upazillas: [],
      fetchAreaStatus: statusTypes.idle,
      areas: [],
    }),
    resetAreaData: (state) => ({
      ...state,
      fetchAreaStatus: statusTypes.idle,
      areas: [],
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDivisions.pending, (state) => {
        state.fetchDivisionStatus = statusTypes.loading;
        state.divisions = [];
      })
      .addCase(getDivisions.fulfilled, (state, action) => {
        state.fetchDivisionStatus = statusTypes.succeeded;
        state.divisions = action.payload?.data || [];
      })
      .addCase(getDivisions.rejected, (state, action) => {
        state.fetchDivisionStatus = statusTypes.failed;
        state.divisions = [];
        state.error = action.payload?.error || 'Failed to get divisions';
      })

      .addCase(getDistricts.pending, (state) => {
        state.fetchDistrictStatus = statusTypes.loading;
        state.districts = [];
      })
      .addCase(getDistricts.fulfilled, (state, action) => {
        state.fetchDistrictStatus = statusTypes.succeeded;
        state.districts = action.payload?.data || [];
      })
      .addCase(getDistricts.rejected, (state, action) => {
        state.fetchDistrictStatus = statusTypes.failed;
        state.districts = [];
        state.error = action.payload?.error || 'Failed to get districts';
      })

      .addCase(getUpazillas.pending, (state) => {
        state.fetchUpazillaStatus = statusTypes.loading;
        state.upazillas = [];
      })
      .addCase(getUpazillas.fulfilled, (state, action) => {
        state.fetchUpazillaStatus = statusTypes.succeeded;
        state.upazillas = action.payload?.data || [];
      })
      .addCase(getUpazillas.rejected, (state, action) => {
        state.fetchUpazillaStatus = statusTypes.failed;
        state.upazillas = [];
        state.error = action.payload?.error || 'Failed to get upazillas';
      })

      .addCase(getAreas.pending, (state) => {
        state.fetchAreaStatus = statusTypes.loading;
        state.areas = [];
      })
      .addCase(getAreas.fulfilled, (state, action) => {
        state.fetchAreaStatus = statusTypes.succeeded;
        state.areas = action.payload?.data || [];
      })
      .addCase(getAreas.rejected, (state, action) => {
        state.fetchAreaStatus = statusTypes.failed;
        state.areas = [];
        state.error = action.payload?.error || 'Failed to get areas';
      })

      // user all addresses
      .addCase(getUserAddresses.pending, (state) => {
        state.fetchUserAddressesStatus = statusTypes.idle;
        state.userAddresses = [];
      })
      .addCase(getUserAddresses.fulfilled, (state, action) => {
        state.fetchUserAddressesStatus = statusTypes.succeeded;
        state.userAddresses = action.payload?.data || [];

        // only set if current address is empty
        if (state.currentAddress?.id === '') {
          if (action.payload?.data?.length > 0) {
            state.currentAddress = action.payload?.data[0];
          }
        }
      })
      .addCase(getUserAddresses.rejected, (state, action) => {
        state.fetchUserAddressesStatus = statusTypes.failed;
        state.userAddresses = [];
        state.error = action.payload?.error || 'Failed to get user addresses';
      })

      // Post shipping address
      .addCase(postShippingAddress.pending, (state) => {
        state.postStatus = statusTypes.loading;
      })
      .addCase(postShippingAddress.fulfilled, (state, action) => {
        const { data, formType, userType } = action.payload;

        state.postStatus = statusTypes.succeeded;
        state.currentAddShippingAddressFormType = formType;

        if (userType === userTypes.guest) {
          state.currentAddress = data;
        }
      })
      .addCase(postShippingAddress.rejected, (state, action) => {
        state.postStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to post shipping address.';
      })

      // edit shipping address
      .addCase(EditShippingAddress.pending, (state) => {
        state.editStatus = statusTypes.loading;
        state.error = '';
      })
      .addCase(EditShippingAddress.fulfilled, (state, action) => {
        const { formType, updatedFormData } = action.payload;

        const editData = updatedFormData.division.id
          ? { ...updatedFormData }
          : getEditFormattedData(updatedFormData);

        state.editStatus = statusTypes.succeeded;
        state.currentAddress = { ...editData };
        state.currentAddShippingAddressFormType = formType;
      })
      .addCase(EditShippingAddress.rejected, (state, action) => {
        state.editStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to edit shipping address.';
      })

      // delete shipping address
      .addCase(DeleteShippingAddress.pending, (state) => {
        state.deleteStatus = statusTypes.loading;
        state.error = '';
      })
      .addCase(DeleteShippingAddress.fulfilled, (state, action) => {
        const shippingId = action.payload;
        const updatedAddresses = state.userAddresses.filter(
          (address) => address.id !== shippingId,
        );
        state.userAddresses = updatedAddresses;

        // Reset current address if deleted address was selected
        if (state.currentAddress?.id === shippingId) {
          state.currentAddress = updatedAddresses[0] || initialAddress;
        }

        state.deleteStatus = statusTypes.deleted;
      })
      .addCase(DeleteShippingAddress.rejected, (state, action) => {
        state.deleteStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to delete shipping address.';
      })

      // get shipping fees
      .addCase(getShippingFees.pending, (state) => {
        state.fetchShippingFeesStatus = statusTypes.loading;
      })
      .addCase(getShippingFees.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.fetchShippingFeesStatus = statusTypes.succeeded;
      })
      .addCase(getShippingFees.rejected, (state, action) => {
        state.fetchShippingFeesStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to get shipping fees.';
      });
  },
});

export const {
  setSelectedAddress,
  updateCurrentAddress,
  updateBillingAddress,
  removeBillingAddress,

  toggleIsSelectDifferentBillingAddress,
  setIsOpenUserAddressesModal,
  setIsOpenAddAddressModal,
  setIsOpenEditAddressModal,
  setIsOpenDeleteAddressConfirmationModal,

  resetCheckoutCommonMessage,
  // setCheckoutCommonMessage,

  updatePostStatus,
  updateEditStatus,
  updateDeleteStatus,

  resetPostStatus,
  resetEditStatus,
  resetDeleteStatus,
  resetShippingInfoForAuthenticateUser,
  resetShippingInfoAfterOrderSucceeded,
  resetShippingAddressForCache,
  resetDivisionData,
  resetDistrictData,
  resetUpazillaData,
  resetAreaData,
} = shippingAddressSlice.actions;

export default shippingAddressSlice.reducer;
